import { useCallback, useMemo } from "react"

import { SortOrder, SortT } from "./helpers"
import { UseSortProps, UseSortRes } from "./types"
import { useSortFilterState } from "../../context/SortFilterProvider"

interface UseBaseSortProps<ItemT, OrderT> extends UseSortProps<ItemT, OrderT> {
  sortValue: string | null
  orderValue: SortOrder | null
  setSortValue: (value: string | null) => void
  setOrderValue: (value: SortOrder | null) => void
}

export function useBaseSort<ItemT, OrderT>({
  items,
  keyExtractor,
  labelExtractor,
  accessoryExtractor,
  orderExtractor,
  defaultOrder = SortOrder.Ascending,
  sortValue,
  orderValue,
  setSortValue,
  setOrderValue,
}: UseBaseSortProps<ItemT, OrderT>): UseSortRes<ItemT, OrderT> {
  const onSelectSortBy = useCallback(
    (sort: ItemT | null) => {
      if (sort) {
        setSortValue(keyExtractor(sort))
        if (!orderValue) {
          setOrderValue(defaultOrder)
        }
      } else {
        setSortValue(null)
        setOrderValue(null)
      }
    },
    [keyExtractor, orderValue, setSortValue, setOrderValue, defaultOrder]
  )

  const onSelectDirection = useCallback(
    (direction: SortOrder) => {
      setOrderValue(direction)
    },
    [setOrderValue]
  )

  const sort = useMemo<SortT<ItemT> | null>(() => {
    if (!sortValue) return null
    const value = items.find((item) => keyExtractor(item) === sortValue)
    if (!value) return null
    return {
      value,
      order: orderValue ?? defaultOrder,
    }
  }, [items, sortValue, orderValue, keyExtractor, defaultOrder])

  const order = useMemo<OrderT>(
    () => orderExtractor(orderValue),
    [orderValue, orderExtractor]
  )

  return {
    sortProps: {
      sort,
      items,
      onSelectSortBy,
      keyExtractor,
      valueExtractor: labelExtractor,
      accessoryExtractor,
      onSelectDirection,
    },
    order,
  }
}

export function useContextSort<ItemT, OrderT>(
  props: UseSortProps<ItemT, OrderT>
): UseSortRes<ItemT, OrderT> {
  const { sortBy, setSortBy, sortOrder, setSortOrder } = useSortFilterState()

  return useBaseSort({
    ...props,
    sortValue: sortBy,
    orderValue: sortOrder,
    setSortValue: setSortBy,
    setOrderValue: setSortOrder,
  })
}
